import { useEffect, useState } from "react";
import type { Device } from "@/mocks/devices";
import { apiGet } from "@/lib/api";
import { wgs84ToGcj02 } from "@/lib/geo";

interface TrackPoint { at: string; lat: number; lng: number; battery: number }

// 高德 marker 链接：存储是 WGS-84（GPS 原始），跳地图前转 GCJ-02。
function amapMarker(lat: number, lng: number, name: string): string {
  const [gLat, gLng] = wgs84ToGcj02(lat, lng);
  return `https://uri.amap.com/marker?position=${gLng.toFixed(6)},${gLat.toFixed(6)}&name=${encodeURIComponent(name)}`;
}

interface DeviceDetailDrawerProps {
  device: Device | null;
  onClose: () => void;
}

function DeviceDetailDrawer({ device, onClose }: DeviceDetailDrawerProps) {
  const [activeTab, setActiveTab] = useState<"info" | "history" | "maintenance">("info");
  const [track, setTrack] = useState<TrackPoint[]>([]);
  const hasPos = !!device && !!device.lastLat && !!device.lastLng;

  useEffect(() => {
    setTrack([]);
    if (!device?.id) return;
    apiGet<TrackPoint[]>(`/devices/${device.id}/track?hours=24`)
      .then((t) => setTrack(t ?? []))
      .catch(() => {});
  }, [device?.id]);

  if (!device) return null;

  const statusConfig = {
    online: { label: "在线", color: "bg-emerald-100 text-emerald-700" },
    weak: { label: "弱连接", color: "bg-amber-100 text-amber-700" },
    offline: { label: "离线", color: "bg-gray-100 text-gray-700" },
    low_battery: { label: "低电", color: "bg-red-100 text-red-700" },
    maintenance: { label: "维护中", color: "bg-purple-100 text-purple-700" },
  };

  const status = statusConfig[device.status];

  const batteryColor = device.battery > 50 ? "bg-emerald-500" : device.battery > 20 ? "bg-amber-500" : "bg-red-500";

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      
      {/* Drawer */}
      <div className="relative w-full max-w-full md:max-w-xl bg-white h-full shadow-xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">设备详情</h2>
            <p className="text-sm text-gray-500 mt-0.5">{device.deviceNo}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Basic Info Card */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}>
                {status.label}
              </span>
              <span className="text-xs text-gray-400">固件 {device.firmware}</span>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-gray-500 block mb-1">设备号</span>
                <span className="text-sm font-medium text-gray-900 font-mono">{device.deviceNo}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block mb-1">设备类型</span>
                <span className="text-sm font-medium text-gray-900">
                  <i className={`${(device.deviceType ?? "glass") === "phone" ? "ri-smartphone-line" : "ri-glasses-line"} mr-1 text-teal-600`} />
                  {(device.deviceType ?? "glass") === "phone" ? "手机" : "AI眼镜"}
                </span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block mb-1">硬件ID（唯一识别）</span>
                <span className="text-sm font-medium text-gray-900 font-mono">{device.hardwareId || "-"}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block mb-1">MAC 地址</span>
                <span className="text-sm font-medium text-gray-900 font-mono">{device.mac || "-"}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block mb-1">型号</span>
                <span className="text-sm font-medium text-gray-900">{device.model || "-"}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block mb-1">配对设备</span>
                <span className="text-sm font-medium text-gray-900 font-mono">
                  {device.pairedDeviceNo ? (
                    <>
                      <i className="ri-link mr-1 text-teal-500" />
                      {device.pairedDeviceNo}
                    </>
                  ) : (
                    "未配对"
                  )}
                </span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block mb-1">所属船次</span>
                <span className="text-sm font-medium text-gray-900">{device.ship}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block mb-1">分组</span>
                <span className="text-sm font-medium text-gray-900">{device.group}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block mb-1">最近在线</span>
                <span className="text-sm font-medium text-gray-900">{device.lastOnline}</span>
              </div>
            </div>

            {/* Battery */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-gray-500">电量</span>
                <span className="text-xs font-medium text-gray-900">{device.battery}%</span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${batteryColor}`} style={{ width: `${device.battery}%` }} />
              </div>
            </div>

            {/* Last reported position (WGS-84 from App heartbeat) */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-gray-500">最新位置（GPS / WGS-84）</span>
                {track.length > 0 && (
                  <span className="text-xs text-gray-400">近24h上报 {track.length} 个点</span>
                )}
              </div>
              {hasPos ? (
                <div className="flex items-center gap-3">
                  <span className="text-sm font-mono text-gray-900">
                    {device.lastLat!.toFixed(6)}, {device.lastLng!.toFixed(6)}
                  </span>
                  <a
                    href={amapMarker(device.lastLat!, device.lastLng!, device.deviceNo)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-teal-600 hover:text-teal-700 inline-flex items-center gap-1"
                  >
                    <i className="ri-map-pin-line" />在高德查看
                  </a>
                </div>
              ) : (
                <span className="text-sm text-gray-400">暂未上报</span>
              )}
            </div>

            {/* Pairing Code */}
            {device.pairingCode && (
              <div className="mt-4 flex items-center gap-3">
                <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-2">
                  <span className="text-sm font-mono font-medium text-gray-900">{device.pairingCode}</span>
                  <button className="text-gray-400 hover:text-teal-600 transition-colors">
                    <i className="ri-file-copy-line" />
                  </button>
                </div>
                <span className="text-xs text-gray-500">配对码</span>
              </div>
            )}
          </div>

          {/* Binding Info */}
          {device.boundVisitor && (
            <div className="bg-teal-50 rounded-lg p-4 mb-6">
              <h4 className="text-sm font-medium text-teal-900 mb-3">当前绑定</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-xs text-teal-600 block mb-1">游客</span>
                  <span className="text-sm font-medium text-teal-900">{device.boundVisitor}</span>
                </div>
                <div>
                  <span className="text-xs text-teal-600 block mb-1">行程</span>
                  <span className="text-sm font-medium text-teal-900">{device.trip}</span>
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-4">
            <div className="flex gap-1">
              {[
                { key: "info" as const, label: "基本信息" },
                { key: "history" as const, label: "绑定历史" },
                { key: "maintenance" as const, label: "维护记录" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? "border-teal-600 text-teal-700"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === "info" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">设备序列号</span>
                <span className="text-sm font-medium text-gray-900">{device.deviceNo}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">MAC 地址</span>
                <span className="text-sm font-medium text-gray-900 font-mono">{device.mac}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">硬件型号</span>
                <span className="text-sm font-medium text-gray-900">{device.model}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">固件版本</span>
                <span className="text-sm font-medium text-gray-900">{device.firmware}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">所属船次</span>
                <span className="text-sm font-medium text-gray-900">{device.ship}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">当前分组</span>
                <span className="text-sm font-medium text-gray-900">{device.group}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-500">最近在线时间</span>
                <span className="text-sm font-medium text-gray-900">{device.lastOnline}</span>
              </div>
              {device.pairingCode && (
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">当前配对码</span>
                  <span className="text-sm font-medium text-gray-900 font-mono">{device.pairingCode}</span>
                </div>
              )}
              {device.boundVisitor && (
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">绑定游客</span>
                  <span className="text-sm font-medium text-gray-900">{device.boundVisitor}</span>
                </div>
              )}
              {device.trip && (
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">所属行程</span>
                  <span className="text-sm font-medium text-gray-900">{device.trip}</span>
                </div>
              )}
            </div>
          )}

          {activeTab === "history" && (
            <div className="space-y-3">
              {[
                { time: "2026-06-01 14:32:18", event: "已配对，绑定游客陈明远", type: "pair" },
                { time: "2026-06-01 08:30:00", event: "设备上线，固件 v2.3.1", type: "online" },
                { time: "2026-05-31 18:45:12", event: "已解绑，游客行程结束", type: "unpair" },
                { time: "2026-05-31 08:15:00", event: "已配对，绑定游客李明", type: "pair" },
                { time: "2026-05-30 22:10:00", event: "设备离线，进入充电模式", type: "offline" },
              ].map((record, index) => (
                <div key={index} className="flex items-start gap-3 py-2">
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                    record.type === "pair" ? "bg-teal-500" :
                    record.type === "online" ? "bg-emerald-500" :
                    record.type === "unpair" ? "bg-amber-500" :
                    "bg-gray-400"
                  }`} />
                  <div className="flex-1">
                    <p className="text-sm text-gray-800">{record.event}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{record.time}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "maintenance" && (
            <div className="space-y-3">
              {[
                { date: "2026-05-15", type: "固件升级", content: "升级至 v2.3.1，修复蓝牙连接稳定性", staff: "张工程师" },
                { date: "2026-04-20", type: "硬件检修", content: "更换电池模组，容量恢复至 100%", staff: "王工程师" },
                { date: "2026-03-10", type: "常规保养", content: "清洁镜片、检查麦克风和扬声器", staff: "李工程师" },
              ].map((record, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500">{record.type}</span>
                    <span className="text-xs text-gray-400">{record.date}</span>
                  </div>
                  <p className="text-sm text-gray-800 mb-2">{record.content}</p>
                  <span className="text-xs text-gray-500">维修人员：{record.staff}</span>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 pt-4 border-t border-gray-200 flex gap-3">
            {device.boundVisitor && (
              <button className="px-4 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors">
                远程解绑
              </button>
            )}
            <button className="px-4 py-2 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-100 transition-colors">
              生成配对码
            </button>
            <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">
              停用设备
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeviceDetailDrawer;